
  import { defineComponent, PropType } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';

  import { DEFAULT_BASE_FILTER } from '../../constants/constants';
  import {
    ALL_YEAR_OPTION,
    DASHBOARDS_RATING_CARD_TITLES,
    VES_EXPORT_REGION_EFFECTIVNESS_RATING_EXCEL_SORT_ORDER,
  } from '../../constants/dashboards';
  import { DASHBOARDS_VES_ACTIONS_NAME, DASHBOARDS_VES_GETTERS_NAME } from '../../store/dashboards/name';
  import { TBaseRegionFilter, TRatingRegionFilter } from '../../types/common';
  import { TDashboardMainFilter, TRatingElement, TRegionMapItem, TTopEffectiveRegion } from '../../types/dashboards';
  import { getRatingListWithIndex } from '../../utils/rating';
  import BaseFilterBlock from '../common/BaseFilterBlock.vue';
  import DateActualDataSubtitle from '../common/DateActualDataSubtitle.vue';
  import DashboardsMap from './DashboardsMap.vue';
  import DashboardsRatingCard from './DashboardsRatingCard.vue';
  import DashboardsRatingTooltipContent from './DashboardsRatingTooltipContent.vue';

  type TRegionMapItemWithRating = TRegionMapItem & {
    place: number;
  };

  export default defineComponent({
    name: 'DashboardsMapBlock',
    components: {
      BaseFilterBlock,
      ButtonComponent,
      DashboardsMap,
      DashboardsRatingCard,
      DashboardsRatingTooltipContent,
      DateActualDataSubtitle,
    },
    props: {
      filter: {
        type: Object as PropType<TDashboardMainFilter>,
        required: true,
      },
      isSimpleYearLabel: {
        type: Boolean,
        default: false,
      },
    },
    data(): {
      topEffectiveRegionsAbortController: AbortController;
      topEffectiveRegionsRatingAbortController: AbortController;
      baseFilter: TBaseRegionFilter;
      isMapLoading: boolean;
      isRatingLoading: boolean;
      exportRegionEffectivnessRatingExcelSortOrder: typeof VES_EXPORT_REGION_EFFECTIVNESS_RATING_EXCEL_SORT_ORDER;
      ratingCardTitles: typeof DASHBOARDS_RATING_CARD_TITLES;
    } {
      return {
        topEffectiveRegionsAbortController: new AbortController(),
        topEffectiveRegionsRatingAbortController: new AbortController(),
        baseFilter: { ...DEFAULT_BASE_FILTER },
        isMapLoading: false,
        isRatingLoading: false,
        exportRegionEffectivnessRatingExcelSortOrder: VES_EXPORT_REGION_EFFECTIVNESS_RATING_EXCEL_SORT_ORDER,
        ratingCardTitles: DASHBOARDS_RATING_CARD_TITLES,
      };
    },
    computed: {
      ...mapGetters({
        topEffectiveRegions: DASHBOARDS_VES_GETTERS_NAME.getTopEffectiveRegions,
        topEffectiveRegionsRating: DASHBOARDS_VES_GETTERS_NAME.getTopEffectiveRegionsRating,
      }),

      topEffectiveRegionsRatingWithIndex(): TRatingElement[] {
        return getRatingListWithIndex(this.topEffectiveRegionsRating, this.topEffectiveRegions);
      },

      getTopEffectiveRegionsRatingReverseWithIndex(): TRatingElement[] {
        return [...this.topEffectiveRegionsRatingWithIndex].reverse();
      },
      getAllFilter(): TBaseRegionFilter & TDashboardMainFilter {
        return { ...this.baseFilter, ...this.filter };
      },
      getRatingFilter(): TRatingRegionFilter {
        return { ...this.filter };
      },
      getIsDisableButton(): boolean {
        return !this.baseFilter.regionRfId;
      },
      getTopEffectiveRegionsRatingMap(): Map<number, number> {
        return new Map(
          this.topEffectiveRegionsRating.map((item: TRatingElement, index: number) => [item.id, index + 1]),
        );
      },
      getTopEffectiveRegionsWithRating(): TRegionMapItemWithRating[] {
        return this.topEffectiveRegions.map((item: TTopEffectiveRegion) => {
          return {
            ...item,
            place: this.getTopEffectiveRegionsRatingMap.get(item.regionId),
          };
        });
      },
      isShowDynamicInRatingCards(): boolean {
        return this.filter.year !== ALL_YEAR_OPTION.value;
      },
    },
    methods: {
      ...mapActions({
        getTopEffectiveRegions: DASHBOARDS_VES_ACTIONS_NAME.getTopEffectiveRegions,
        getTopEffectiveRegionsRating: DASHBOARDS_VES_ACTIONS_NAME.getTopEffectiveRegionsRating,
      }),
      changeBaseFilter(value: TBaseRegionFilter) {
        this.baseFilter = value;
      },
      openCard() {
        this.$router.push({
          name: 'RegionDashboardsVES',
          params: { regionRfId: this.baseFilter.regionRfId },
          query: { year: this.filter.year },
        });
      },
    },
    watch: {
      getAllFilter: {
        async handler(newValue) {
          this.topEffectiveRegionsAbortController.abort();
          this.topEffectiveRegionsAbortController = new AbortController();

          const payload = {
            params: newValue,
            abortController: this.topEffectiveRegionsAbortController,
          };

          this.isMapLoading = true;
          await this.getTopEffectiveRegions(payload);
          this.isMapLoading = false;
        },
        immediate: true,
        deep: true,
      },
      getRatingFilter: {
        async handler(newValue) {
          this.topEffectiveRegionsRatingAbortController.abort();
          this.topEffectiveRegionsRatingAbortController = new AbortController();

          const payload = {
            params: newValue,
            abortController: this.topEffectiveRegionsRatingAbortController,
          };

          this.isRatingLoading = true;
          await this.getTopEffectiveRegionsRating(payload, payload);
          this.isRatingLoading = false;
        },
        immediate: true,
        deep: true,
      },
    },
  });
